import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import type { SxProps, Theme } from "@mui/material";
import { Box, Skeleton, Stack } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import type { SmartViewArtifact } from "../../../../../api/types/smartViewArtifactsTypes";
import type { ArtifactPreviewStatus } from "./useArtifactPreview";
import useArtifactPreview from "./useArtifactPreview";

const NEAR_VIEWPORT_MARGIN = "200px";

const fillSx: SystemStyleObject<Theme> = { position: "absolute", inset: 0, width: "100%", height: "100%" };

interface PreviewContentProps {
  status: ArtifactPreviewStatus;
  url?: string;
}

const PreviewContent = ({ status, url }: PreviewContentProps) => {
  if (status === "loading") {
    return <Skeleton variant="rectangular" sx={fillSx} />;
  }
  if (status === "ready") {
    return <Box component="img" src={url} alt="" sx={{ ...fillSx, display: "block", objectFit: "cover" }} />;
  }
  return (
    <Stack sx={{ ...fillSx, alignItems: "center", justifyContent: "center", bgcolor: "background.grey" }}>
      <SpaceDashboardOutlinedIcon color="disabled" />
    </Stack>
  );
};

interface Props {
  artifact: SmartViewArtifact;
  loadOnMount?: boolean;
  observerRoot?: Element;
  sx?: SxProps<Theme>;
}

const ArtifactPreviewImage = ({ artifact, loadOnMount, observerRoot, sx }: Props) => {
  const [isNearViewport, setIsNearViewport] = useState(loadOnMount ?? false);
  const boxRef = useRef<HTMLDivElement>(null);
  const version = artifact.publishedVersion ?? artifact.latestVersion;
  const hasPicture = artifact.pictureVersions.includes(version);
  const preview = useArtifactPreview(artifact.id, version, isNearViewport && hasPicture);

  useEffect(() => {
    const box = boxRef.current;
    if (!box || isNearViewport) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsNearViewport(true);
        }
      },
      { root: observerRoot, rootMargin: NEAR_VIEWPORT_MARGIN }
    );
    observer.observe(box);
    return () => observer.disconnect();
  }, [isNearViewport, observerRoot]);

  return (
    <Box
      ref={boxRef}
      sx={[{ position: "relative", aspectRatio: "16 / 10", overflow: "hidden" }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <PreviewContent status={hasPicture ? preview.status : "missing"} url={preview.url} />
    </Box>
  );
};

export default ArtifactPreviewImage;
